import { supabase } from '@/integrations/supabase/client';
import {
    IPaymentPlan,
    SavePaymentPlanPayload,
    SendInstallmentReminderResult,
} from '@/lib/builder/models/payment-plan.model';

/**
 * Accesso al piano di pagamento a rate di un itinerario.
 *
 * Tutto passa da RPC: la validazione "le rate coprono il totale" deve girare contro
 * itineraries.total letto dal database, non contro un totale calcolato nel browser.
 */

export const fetchPaymentPlan = async (itineraryId: string): Promise<IPaymentPlan | null> => {
    const { data, error } = await supabase.rpc('get_itinerary_payment_plan', {
        p_itinerary_id: itineraryId,
    });

    if (error) throw error;
    return (data as unknown as IPaymentPlan) ?? null;
};

export const savePaymentPlan = async (payload: SavePaymentPlanPayload): Promise<IPaymentPlan> => {
    const { data, error } = await supabase.rpc('save_itinerary_payment_plan', {
        p_plan: payload as unknown as Record<string, unknown>,
    });

    if (error) throw error;
    return data as unknown as IPaymentPlan;
};

export const deletePaymentPlan = async (itineraryId: string): Promise<boolean> => {
    const { data, error } = await supabase.rpc('delete_itinerary_payment_plan', {
        p_itinerary_id: itineraryId,
    });

    if (error) throw error;
    return Boolean(data);
};

/**
 * Registra l'incasso di una rata pagata fuori piattaforma (bonifico).
 * Il pagamento con carta non passa di qui: lo scrive il webhook Stripe.
 *
 * Ritorna la commissione Place2Be rimasta a debito su questo incasso. Il chiamante la
 * rilegge da qui e non dal proprio calcolo: l'anteprima nella modale usa l'aliquota che
 * conosce il browser, questo e' il numero che e' finito in `payments`.
 */
export const markInstallmentPaid = async (params: {
    installmentId: string;
    paidAmount?: number;
    paymentMethod?: string;
    paidAt?: string;
}): Promise<{ platformFeeAmount: number | null }> => {
    const { data, error } = await supabase.rpc('mark_installment_paid', {
        p_installment_id: params.installmentId,
        p_payment_id: null,
        p_paid_amount: params.paidAmount ?? null,
        p_payment_method: params.paymentMethod ?? 'bank_transfer',
        p_paid_at: params.paidAt ?? new Date().toISOString(),
    });

    if (error) throw error;

    const fee = (data as { platform_fee_amount?: number | null } | null)?.platform_fee_amount;
    return { platformFeeAmount: fee ?? null };
};

/**
 * Manda subito al cliente il promemoria di una rata.
 *
 * Il template lo sceglie il database dalla scadenza (sollecito se gia' passata), non il
 * chiamante: il browser ragionerebbe in UTC, la scadenza e' una data italiana.
 */
export const sendInstallmentReminder = async (
    installmentId: string
): Promise<SendInstallmentReminderResult> => {
    const { data, error } = await supabase.rpc('send_installment_reminder_now', {
        p_installment_id: installmentId,
    });

    if (error) throw error;
    return data as unknown as SendInstallmentReminderResult;
};

/**
 * Traduce gli errori sollevati dalle RPC in chiavi i18n.
 * I messaggi del database sono identificatori stabili proprio per questo: non vanno
 * mai mostrati all'utente cosi' come sono.
 */
export const paymentPlanErrorKey = (error: unknown): string => {
    const message = (error as { message?: string })?.message ?? '';

    const known = [
        'forbidden',
        'total_mismatch',
        'due_dates_not_increasing',
        'single_mode_requires_one_installment',
        'at_least_one_installment',
        'installment_already_paid',
        'plan_has_paid_installments',
        'due_date_required',
        'itinerary_not_found',
        'installment_not_pending',
        'reminder_throttled',
        'installment_payment_processing',
        'no_recipient',
        'missing_email_template',
    ];

    const match = known.find((key) => message.includes(key));
    return match ? `checkout.paymentPlan.errors.${match}` : 'checkout.paymentPlan.errors.generic';
};
